import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { translations } from '../i18n/translations'

type Lang = 'pl' | 'en' | 'de'

interface LanguageContextValue {
  lang: Lang
  setLang: (l: Lang) => void
  t: (key: string, fallback?: string) => string
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

const STORAGE_KEY = 'fb_lang'

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>('pl')

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Lang
    if (saved === 'pl' || saved === 'en' || saved === 'de') {
      setLangState(saved)
    } else {
      const browser = (navigator.language || '').toLowerCase().split('-')[0]
      const detected: Lang = browser === 'pl' ? 'pl' : browser === 'de' ? 'de' : 'en'
      setLangState(detected)
      try { localStorage.setItem(STORAGE_KEY, detected) } catch {}
    }
  }, [])

  const setLang = (l: Lang) => {
    setLangState(l)
    try { localStorage.setItem(STORAGE_KEY, l) } catch {}
  }

  const t = (key: string, fallback?: string) => {
    const dict = translations[lang] || {}
    return (dict[key] as string) || fallback || key
  }

  const value = useMemo(() => ({ lang, setLang, t }), [lang])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}

